import { useEffect, useState } from 'react';
import { Check, Plus, Trash2, X, Pencil, ChevronDown, ChevronRight, ChevronUp, GripVertical, Palette } from 'lucide-react';
import { toast } from 'sonner';
import {
  COLOR_PALETTE,
  setAreaColor,
  addArea,
  removeArea,
  listKnownAreas,
  getAllAreasMerged,
  reorderAreas,
} from '../../lib/areaColors';
import { updateArea } from '../../lib/api';

const DEFAULT_KEYS = new Set(['business', 'lavoro', 'personale', 'salute']);
const STORAGE_OPEN = 'qp.area_manager_open';

/**
 * AreaManager — pannello inline da incorporare nella Dashboard.
 *
 * Features:
 *  - Toggle collassabile (chevron) con stato persistito su localStorage.
 *  - Cambio colore: palette 12 preset + native `<input type="color">` per hex arbitrari.
 *  - Rinomina inline (click sulla label).
 *  - Aggiunta/eliminazione aree custom.
 *  - Drag-and-drop HTML5 per riordinare (persistito su DB via PATCH ordine).
 *
 * Sincronizzato con `aree_utente` su Supabase via lib/areaColors.js.
 */
export default function AreaManager() {
  const [, force] = useState(0);
  const [open, setOpen] = useState(() => {
    try {
      const v = localStorage.getItem(STORAGE_OPEN);
      return v === null ? true : v === '1';
    } catch {
      return true;
    }
  });
  const [openKey, setOpenKey] = useState(null);
  const [editingLabel, setEditingLabel] = useState(null);
  const [labelDraft, setLabelDraft] = useState('');
  const [adding, setAdding] = useState(false);
  const [newLabel, setNewLabel] = useState('');
  const [newColor, setNewColor] = useState(COLOR_PALETTE[5]);
  const [busy, setBusy] = useState(false);
  const [dragKey, setDragKey] = useState(null);
  const [dragOverKey, setDragOverKey] = useState(null);

  useEffect(() => {
    const onChange = () => force((n) => n + 1);
    window.addEventListener('qp:areas-changed', onChange);
    return () => window.removeEventListener('qp:areas-changed', onChange);
  }, []);

  useEffect(() => {
    try { localStorage.setItem(STORAGE_OPEN, open ? '1' : '0'); } catch { /* noop */ }
  }, [open]);

  const areas = listKnownAreas();
  const meta = getAllAreasMerged();

  const pickColor = async (key, color) => {
    const res = await setAreaColor(key, color);
    setOpenKey(null);
    if (res.ok) {
      toast.success(`Colore aggiornato per ${meta[key].label}`);
    } else {
      toast.error(res.error || 'Errore aggiornamento colore');
    }
  };

  const startRename = (key) => {
    setEditingLabel(key);
    setLabelDraft(meta[key]?.label || '');
  };

  const saveRename = async (key) => {
    const label = (labelDraft || '').trim();
    if (!label) {
      setEditingLabel(null);
      return;
    }
    try {
      await updateArea(key, { label });
      meta[key] = { ...meta[key], label };
      setEditingLabel(null);
      force((n) => n + 1);
      window.dispatchEvent(new CustomEvent('qp:areas-changed', { detail: { key, label } }));
      toast.success('Etichetta aggiornata');
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Errore aggiornamento';
      toast.error(msg);
    }
  };

  const submitNew = async () => {
    const label = newLabel.trim();
    if (!label) {
      toast.error('Inserisci un nome per l\'area');
      return;
    }
    if (busy) return;
    setBusy(true);
    const res = await addArea(null, label, newColor);
    setBusy(false);
    if (res.ok) {
      toast.success(`Area "${label}" creata`);
      setAdding(false);
      setNewLabel('');
      setNewColor(COLOR_PALETTE[5]);
    } else {
      toast.error(res.error || 'Errore creazione');
    }
  };

  const submitDelete = async (key) => {
    if (DEFAULT_KEYS.has(key)) {
      toast.error('Le aree di default non possono essere eliminate');
      return;
    }
    if (!window.confirm(`Eliminare l'area "${meta[key]?.label || key}"?\nGli obiettivi/azioni esistenti rimarranno ma perderanno il riferimento al colore.`)) {
      return;
    }
    const res = await removeArea(key);
    if (res.ok) {
      toast.success('Area eliminata');
    } else {
      toast.error(res.error || 'Errore eliminazione');
    }
  };

  // ── Drag & Drop ──────────────────────────────────────────────────────────
  const onDragStart = (e, key) => {
    setDragKey(key);
    e.dataTransfer.effectAllowed = 'move';
    try { e.dataTransfer.setData('text/plain', key); } catch { /* noop */ }
  };
  const onDragOver = (e, key) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (dragOverKey !== key) setDragOverKey(key);
  };
  const onDragLeave = (e) => {
    if (e.currentTarget === e.target) setDragOverKey(null);
  };
  const onDrop = async (e, targetKey) => {
    e.preventDefault();
    const fromKey = dragKey || e.dataTransfer.getData('text/plain');
    setDragKey(null);
    setDragOverKey(null);
    if (!fromKey || fromKey === targetKey) return;
    const order = listKnownAreas();
    const fromIdx = order.indexOf(fromKey);
    const toIdx = order.indexOf(targetKey);
    if (fromIdx === -1 || toIdx === -1) return;
    const next = [...order];
    next.splice(fromIdx, 1);
    next.splice(toIdx, 0, fromKey);
    const res = await reorderAreas(next);
    if (!res.ok) {
      toast.error(res.error || 'Errore riordino');
    } else {
      toast.success('Ordine aree aggiornato');
    }
  };
  const onDragEnd = () => {
    setDragKey(null);
    setDragOverKey(null);
  };

  // Alternativa touch/tastiera al drag: sposta su/giù di una posizione
  const moveArea = async (key, delta) => {
    const order = listKnownAreas();
    const fromIdx = order.indexOf(key);
    const toIdx = fromIdx + delta;
    if (fromIdx === -1 || toIdx < 0 || toIdx >= order.length) return;
    const next = [...order];
    next.splice(fromIdx, 1);
    next.splice(toIdx, 0, key);
    const res = await reorderAreas(next);
    if (!res.ok) toast.error(res.error || 'Errore riordino');
    else toast.success('Ordine aree aggiornato');
  };

  return (
    <section className="mb-12" data-testid="area-manager">
      <div className="flex items-baseline justify-between mb-4 border-b border-line pb-3">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="inline-flex items-center gap-2 text-left"
          data-testid="area-manager-toggle"
          aria-expanded={open}
        >
          {open ? <ChevronDown size={16} strokeWidth={1.5} /> : <ChevronRight size={16} strokeWidth={1.5} />}
          <h2 className="font-display text-xl md:text-2xl tracking-tight">Aree</h2>
          {!open && (
            <span className="ml-2 font-mono text-[10px] uppercase tracking-[0.18em] text-mute">
              {areas.length} aree
            </span>
          )}
        </button>
        {open && (
          <button
            type="button"
            onClick={() => setAdding((v) => !v)}
            className="inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.18em] text-soft hover:text-ink transition-colors"
            data-testid="area-manager-add-toggle"
          >
            {adding ? <X size={12} /> : <Plus size={12} />}
            {adding ? 'Annulla' : 'Nuova area'}
          </button>
        )}
      </div>

      {open && adding && (
        <div className="mb-4 p-4 border border-line bg-canvas" data-testid="area-manager-new-form">
          <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-soft mb-2">Crea area</div>
          <div className="flex flex-wrap items-center gap-3">
            <input
              type="text"
              value={newLabel}
              onChange={(e) => setNewLabel(e.target.value)}
              placeholder="Nome (es. Finanza)"
              className="flex-1 min-w-[200px] px-3 py-2 border border-line focus:border-ink outline-none font-body text-sm"
              data-testid="area-manager-new-label"
              maxLength={40}
              autoFocus
            />
            <div
              className="w-8 h-8 border border-line"
              style={{ backgroundColor: newColor }}
              title={newColor}
              data-testid="area-manager-new-color-preview"
            />
            <div className="flex items-center gap-1 p-1 border border-line">
              {COLOR_PALETTE.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setNewColor(c)}
                  className={
                    'w-5 h-5 rounded-full border transition-transform hover:scale-110 ' +
                    (c.toLowerCase() === newColor.toLowerCase() ? 'border-ink ring-1 ring-ink ring-offset-1' : 'border-line')
                  }
                  style={{ backgroundColor: c }}
                  aria-label={`Colore ${c}`}
                  data-testid={`area-manager-new-preset-${c.replace('#','').toLowerCase()}`}
                />
              ))}
            </div>
            <label className="inline-flex items-center gap-1 px-2 py-1 border border-line cursor-pointer hover:border-ink" title="Scegli colore personalizzato">
              <Palette size={12} />
              <input
                type="color"
                value={newColor}
                onChange={(e) => setNewColor(e.target.value)}
                className="w-5 h-5 cursor-pointer border-0 bg-transparent"
                aria-label="Colore personalizzato"
                data-testid="area-manager-new-color-picker"
              />
            </label>
            <button
              type="button"
              onClick={submitNew}
              disabled={busy}
              className="px-4 py-2 bg-ink text-canvas font-mono text-[11px] uppercase tracking-[0.18em] hover:bg-accent disabled:opacity-50"
              data-testid="area-manager-new-submit"
            >
              {busy ? '...' : 'Crea'}
            </button>
          </div>
          <p className="mt-2 font-body text-xs text-mute">
            Le aree create sono salvate sul tuo account e visibili in tutte le viste.
          </p>
        </div>
      )}

      {open && (
        <ul className="space-y-2" data-testid="area-manager-list">
          {areas.map((key) => {
            const m = meta[key];
            if (!m) return null;
            const isOpen = openKey === key;
            const isDefault = DEFAULT_KEYS.has(key);
            const isRenaming = editingLabel === key;
            const isDragging = dragKey === key;
            const isOver = dragOverKey === key;
            return (
              <li
                key={key}
                draggable
                onDragStart={(e) => onDragStart(e, key)}
                onDragOver={(e) => onDragOver(e, key)}
                onDragLeave={onDragLeave}
                onDrop={(e) => onDrop(e, key)}
                onDragEnd={onDragEnd}
                className={
                  'flex items-center gap-3 py-2 px-2 border border-transparent transition-colors ' +
                  (isDragging ? 'opacity-50 ' : '') +
                  (isOver && !isDragging ? 'border-ink bg-linesoft ' : '')
                }
                data-testid={`area-manager-row-${key}`}
              >
                <span
                  className="cursor-grab active:cursor-grabbing text-mute hover:text-ink shrink-0"
                  title="Trascina per riordinare"
                  data-testid={`area-manager-drag-${key}`}
                >
                  <GripVertical size={14} />
                </span>
                {/* Riordino accessibile (touch/tastiera) accanto al drag */}
                <span className="flex flex-col shrink-0 -ml-1">
                  <button
                    type="button"
                    onClick={() => moveArea(key, -1)}
                    className="p-0.5 text-mute hover:text-ink disabled:opacity-30"
                    disabled={areas.indexOf(key) === 0}
                    aria-label={`Sposta ${m.label} su`}
                    data-testid={`area-manager-up-${key}`}
                  >
                    <ChevronUp size={11} strokeWidth={1.5} />
                  </button>
                  <button
                    type="button"
                    onClick={() => moveArea(key, 1)}
                    className="p-0.5 text-mute hover:text-ink disabled:opacity-30"
                    disabled={areas.indexOf(key) === areas.length - 1}
                    aria-label={`Sposta ${m.label} giù`}
                    data-testid={`area-manager-down-${key}`}
                  >
                    <ChevronDown size={11} strokeWidth={1.5} />
                  </button>
                </span>
                <button
                  type="button"
                  onClick={() => setOpenKey(isOpen ? null : key)}
                  className="w-7 h-7 border border-line hover:border-ink shrink-0"
                  style={{ backgroundColor: m.color }}
                  aria-label={`Cambia colore ${m.label}`}
                  data-testid={`area-manager-swatch-${key}`}
                />
                {isRenaming ? (
                  <div className="flex items-center gap-1">
                    <input
                      type="text"
                      value={labelDraft}
                      onChange={(e) => setLabelDraft(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') saveRename(key);
                        if (e.key === 'Escape') setEditingLabel(null);
                      }}
                      autoFocus
                      className="px-2 py-1 border border-ink outline-none font-body text-sm min-w-[140px]"
                      data-testid={`area-manager-label-input-${key}`}
                      maxLength={40}
                    />
                    <button
                      type="button"
                      onClick={() => saveRename(key)}
                      className="p-1 hover:bg-linesoft"
                      data-testid={`area-manager-label-save-${key}`}
                    >
                      <Check size={14} />
                    </button>
                    <button
                      type="button"
                      onClick={() => setEditingLabel(null)}
                      className="p-1 hover:bg-linesoft"
                      data-testid={`area-manager-label-cancel-${key}`}
                    >
                      <X size={14} />
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => startRename(key)}
                    className="font-body text-sm text-ink min-w-[120px] text-left hover:underline decoration-dotted"
                    title="Clicca per rinominare"
                    data-testid={`area-manager-label-${key}`}
                  >
                    {m.label}
                  </button>
                )}
                <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-mute">
                  {m.color.toUpperCase()}
                </span>
                {isDefault && (
                  <span className="font-mono text-[9px] uppercase tracking-[0.18em] text-mute px-1.5 py-0.5 border border-line">
                    default
                  </span>
                )}
                <div className="flex-1" />
                {/* Picker arbitrario (sempre disponibile) */}
                <label className="p-1 text-mute hover:text-ink cursor-pointer" title="Colore personalizzato">
                  <Palette size={12} />
                  <input
                    type="color"
                    value={m.color}
                    onChange={(e) => pickColor(key, e.target.value)}
                    className="sr-only"
                    aria-label={`Colore personalizzato ${m.label}`}
                    data-testid={`area-manager-color-picker-${key}`}
                  />
                </label>
                {!isRenaming && (
                  <button
                    type="button"
                    onClick={() => startRename(key)}
                    className="p-1 text-mute hover:text-ink"
                    title="Rinomina"
                    data-testid={`area-manager-rename-btn-${key}`}
                  >
                    <Pencil size={12} />
                  </button>
                )}
                {!isDefault && (
                  <button
                    type="button"
                    onClick={() => submitDelete(key)}
                    className="p-1 text-mute hover:text-risk"
                    title="Elimina area"
                    data-testid={`area-manager-delete-${key}`}
                  >
                    <Trash2 size={12} />
                  </button>
                )}
                {isOpen && (
                  <div
                    className="flex items-center gap-1.5 ml-2 p-2 border border-ink bg-canvas"
                    data-testid={`area-manager-palette-${key}`}
                  >
                    {COLOR_PALETTE.map((c) => {
                      const active = c.toLowerCase() === (m.color || '').toLowerCase();
                      return (
                        <button
                          key={c}
                          type="button"
                          onClick={() => pickColor(key, c)}
                          className={
                            'w-6 h-6 rounded-full border transition-transform hover:scale-110 ' +
                            (active ? 'border-ink ring-2 ring-ink ring-offset-1' : 'border-line')
                          }
                          style={{ backgroundColor: c }}
                          aria-label={`Colore ${c}`}
                          data-testid={`area-manager-preset-${key}-${c.replace('#','').toLowerCase()}`}
                        >
                          {active && <Check size={12} strokeWidth={2} className="text-white mx-auto" />}
                        </button>
                      );
                    })}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
